/**
 * setting up a request's merged document.
 */
import {
  call,
  actionChannel,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLeading,
} from 'typed-redux-saga/macro';
import { buffers } from 'redux-saga';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { ensureSetUnlocked } from './setLockGate';
import { ROUTE_DOCUMENT_DETAILS } from '@common-modules/routing';
import { sagaIntl } from '@i18n';

import {
  CREATE_PROJECT_FAILURE,
  CREATE_PROJECT_REQUEST,
  LOAD_PROJECT_FAILURE,
  LOAD_PROJECT_REQUEST,
  LOAD_PROJECT_SUCCESS,
  OPEN_PROJECT_REQUEST,
  UPDATE_PROJECT_FAILURE,
  UPDATE_PROJECT_REQUEST,
  UPDATE_PROJECT_SUCCESS,
  UPDATE_PROJECT_TDO_FAILURE,
  UPDATE_PROJECT_TDO_SUCCESS,
  CREATE_PROJECT_SUCCESS,
  CREATE_PROJECT_TDO_FAILURE,
  CREATE_PROJECT_TDO_SUCCESS,
  DELETE_PROJECT_FAILURE,
  DELETE_PROJECT_REQUEST,
  DELETE_PROJECT_SUCCESS,
  DELETE_PROJECT_TDO_FAILURE,
  DELETE_PROJECT_TDO_SUCCESS,
  WRITE_PROJECT_POINTERS_FAILURE,
  WRITE_PROJECT_POINTERS_SUCCESS,
} from './actions';
import { watchProjectRedaction } from './projectRedactionSaga';
import {
  describeMemberIds,
  describeMembershipChange,
  emitProjectAuditEvent,
} from './auditEvents';
import type { TDOId } from '@common-modules/universal/models/Brands';
import { ProjectPointer } from './models';
import {
  buildProjectTdoName,
  createProjectTdo,
  deleteProjectTdo,
  readProjectMembers,
  updateProjectMembers,
  writeProjectPointers,
} from './services';
import { projectsOfFolder, projectsSdoIdOfFolder } from './selectors';
import {
  selectSelectedFolders,
  selectSelectedTdoFile,
} from '@redact-modules/casemedia/selectors';
import { readFilePageCount } from '@redact-modules/casemedia/pageCount';
import type { FolderStateType } from '@redact-modules/casemedia/store';

/**
 * Pattern matchers for the handshakes below.
 */
const forProject = <
  C extends { type: string } & ((...args: never[]) => {
    type: string;
    payload: unknown;
  }),
>(
  creator: C,
  projectTdoId: TDOId
) => {
  // A TYPE GUARD, so the matched action still narrows: `take(predicate)` is
  // typed as a bare `Action`, and the callers read `payload.members` off it.
  const matches = (candidate: unknown): candidate is ReturnType<C> => {
    const a = candidate as {
      type?: string;
      payload?: { projectTdoId?: unknown };
    };
    return (
      a.type === creator.type &&
      String(a.payload?.projectTdoId) === String(projectTdoId)
    );
  };
  return matches;
};

const forCaseSdo = <
  C extends { type: string } & ((...args: never[]) => {
    type: string;
    payload: unknown;
  }),
>(
  creator: C,
  caseSdoId: string
) => {
  const matches = (candidate: unknown): candidate is ReturnType<C> => {
    const a = candidate as { type?: string; payload?: { caseSdoId?: unknown } };
    return (
      a.type === creator.type && String(a.payload?.caseSdoId) === caseSdoId
    );
  };
  return matches;
};

/**
 * The request's folder as the column has it.
 */
function* findRequestFolder(caseId: string) {
  const selectedFolders = yield* select(selectSelectedFolders);
  return (selectedFolders as FolderStateType[] | undefined)?.find(
    (folder) => folder?.id === caseId || folder?.treeObjectId === caseId
  );
}

/**
 * Each member's original page count, for the project's cached spans.
 *
 * THE LOOKUP LIVES IN A SAGA, not in the service that writes the cache: the
 * files are already reachable from here, and reaching for them from
 * `services.ts` pulled the whole app and user-permissions graph into a module
 * that otherwise only talks to GraphQL — which broke two unrelated suites at
 * import time.
 *
 * SHARED BY SETUP AND EDIT. Only the edit path had it, so a project created
 * and never edited since carried no spans at all, and every reader that falls
 * back to the project's cache answered "cannot say" for the whole release.
 */
function* memberPageCounts(members: readonly TDOId[]) {
  const pageCounts = new Map<string, number | null>();
  for (const member of members) {
    const file = yield* select(selectSelectedTdoFile(member));
    pageCounts.set(String(member), readFilePageCount(file?.details));
  }
  return pageCounts;
}

function* handleCreateProject(
  action: ReturnType<typeof CREATE_PROJECT_REQUEST>
) {
  const intl = sagaIntl();
  const { caseId, requestName, kind, members } = action.payload;
  const failed = (message: string) =>
    [
      put(enqueueSnackbar({ message, variant: 'error' })),
      put(CREATE_PROJECT_FAILURE({ caseId })),
    ] as const;

  const folder = yield* call(findRequestFolder, caseId);
  const caseSdoId = projectsSdoIdOfFolder(folder);
  if (!caseSdoId) {
    // Nothing can point at a project here, and a project TDO nothing points at
    // is invisible forever. Refuse rather than create one. Unassigned Media is
    // the known case (it carries no content templates on purpose); a request
    // whose SDO was purged is the other.
    for (const effect of failed(
      intl.formatMessage({
        id: 'mergedDocumentUnavailable',
        defaultMessage: 'This request cannot hold a merged document.',
      })
    )) {
      yield effect;
    }
    return;
  }

  const existingPointers = projectsOfFolder(folder);
  const name = buildProjectTdoName(requestName);

  yield* put(
    createProjectTdo({
      // The same id the upload path uses as `parentFolderId`.
      parentFolderId: (folder?.id ?? caseId) as string,
      name,
      kind,
      members,
      pageCounts: yield* call(memberPageCounts, members),
    }) as never
  );
  const minted = yield* race({
    ok: take(CREATE_PROJECT_TDO_SUCCESS),
    err: take(CREATE_PROJECT_TDO_FAILURE),
  });
  const projectTdoId = minted.ok?.payload?.createTDO?.id;
  if (!projectTdoId) {
    for (const effect of failed(
      intl.formatMessage({
        id: 'mergedDocumentCreateFailed',
        defaultMessage: 'Could not set up the merged document.',
      })
    )) {
      yield effect;
    }
    return;
  }

  const now = new Date().toISOString();
  const pointers: ProjectPointer[] = [
    ...existingPointers,
    {
      tdoId: projectTdoId,
      kind,
      name,
      createdDateTime: now,
      modifiedDateTime: now,
    },
  ];
  yield* put(writeProjectPointers({ caseSdoId, pointers }) as never);
  // MATCHED ON THE RECORD IT WROTE. A bare take here could be answered by a
  // DIFFERENT request's pointer write — `takeLeading` serialises this watcher,
  // not this watcher against the delete handler, which parks on the same pair.
  // The reward for being wrong is "Merged document set up" over a write that
  // failed, and a project TDO nothing points at.
  const recorded = yield* race({
    ok: take(forCaseSdo(WRITE_PROJECT_POINTERS_SUCCESS, caseSdoId)),
    err: take(forCaseSdo(WRITE_PROJECT_POINTERS_FAILURE, caseSdoId)),
  });
  if (!recorded.ok) {
    // The TDO exists and nothing points at it. Deliberately NOT deleted here:
    // the failure may be transient, and an orphan is invisible (the folder
    // view filters project TDOs), so it costs the user nothing — whereas a
    // delete racing a write that actually landed would destroy a real project.
    for (const effect of failed(
      intl.formatMessage({
        id: 'mergedDocumentRecordFailed',
        defaultMessage:
          'The merged document was created but could not be saved to this request.',
      })
    )) {
      yield effect;
    }
    return;
  }

  yield* put(CREATE_PROJECT_SUCCESS({ caseId, projectTdoId, pointers }));
  /**
   * THE SET'S FIRST FACT. Emitted here rather than from a watcher on
   * CREATE_PROJECT_SUCCESS because that action carries the pointer list, not
   * the membership — and the membership, in order, is the thing worth
   * recording: it is what the first build's page ranges and Bates run came
   * from.
   *
   * BY ID, not by name — see `describeMemberIds`. There used to be a
   * `memberLabels` saga here resolving each member's filename through
   * `selectSelectedTdoFile`; nothing renders a filename any more, so it was N
   * selector effects per write for a field that was discarded, and a `name`
   * left in scope for the next person to reach for.
   */
  yield* call(
    emitProjectAuditEvent,
    projectTdoId,
    'Created merged document',
    `"${name}"; ${describeMemberIds(members)}`
  );
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'mergedDocumentCreated',
        defaultMessage: 'Merged document set up.',
      }),
      variant: 'success',
    })
  );

  // NO NAVIGATION. This used to open the first member for editing, on the
  // grounds that setup is not an outcome and leaving somebody on the folder
  // view gave them nothing to do next. Both halves turned out to be wrong.
}

/**
 * Change which documents the merged document includes.
 */
function* handleUpdateProject(
  action: ReturnType<typeof UPDATE_PROJECT_REQUEST>
) {
  const intl = sagaIntl();
  const { projectTdoId, members, expectedMembers } = action.payload;

  // VE-31538. A membership write replaces the member list outright, so doing
  // it under somebody else's session rewrites the set they are editing. The
  // folder view can issue this with no editor open, which is why the gate
  // reads the lock rather than the editor's published verdict.
  if (!(yield* ensureSetUnlocked(projectTdoId))) {
    yield* put(UPDATE_PROJECT_FAILURE({ projectTdoId }));
    return;
  }

  const pageCounts = yield* call(memberPageCounts, members);

  yield* put(
    updateProjectMembers({
      projectTdoId,
      members,
      expectedMembers,
      pageCounts,
    }) as never
  );
  const saved = yield* race({
    ok: take(forProject(UPDATE_PROJECT_TDO_SUCCESS, projectTdoId)),
    err: take(forProject(UPDATE_PROJECT_TDO_FAILURE, projectTdoId)),
  });
  if (!saved.ok) {
    // THE RACE SAYS SO IN ITS OWN WORDS. "Could not update" reads as a
    // network failure the reviewer should retry, where this one means their
    // list was composed against a set somebody else has since changed —
    // retrying the same list is exactly the wrong response.
    const movedUnderUs = saved.err?.payload.reason === 'moved-under-us';
    yield* put(
      enqueueSnackbar({
        message: movedUnderUs
          ? intl.formatMessage({
              id: 'mergedDocumentMembershipNotSaved',
              defaultMessage:
                'The merged document changed while that was open. Nothing was saved — reopen it to make the change.',
            })
          : intl.formatMessage({
              id: 'mergedDocumentUpdateFailed',
              defaultMessage: 'Could not update the merged document.',
            }),
        variant: 'error',
      })
    );
    yield* put(UPDATE_PROJECT_FAILURE({ projectTdoId }));
    return;
  }

  const previousMembers = saved.ok.payload.previousMembers;

  // NO SUCCESS SNACKBAR. Membership saves itself on every tick, so confirming
  // each one would put a toast on screen for an action whose result is already
  // visible — the box is ticked and the tab's count moved. Failures still
  // speak, because those are the ones nothing on screen would otherwise show.
  yield* put(
    UPDATE_PROJECT_SUCCESS({
      projectTdoId,
      members,
      // The stamp the WRITE put on the record, carried through rather than
      // re-minted downstream — see `UPDATE_PROJECT_SUCCESS.decidedAt`.
      decidedAt: saved.ok.payload.decidedAt,
    })
  );

  /**
   * ONE EVENT PER WRITE, NOT PER TICK (VE-31977).
   *
   * The Documents panel debounces a run of checkbox ticks into a single
   * UPDATE_PROJECT_REQUEST, and `watchMembershipWrites` slides the queue so
   * only the newest survives — so this is already the granularity the
   * reviewer's act had. Three boxes ticked in a row produce one entry reading
   * "Added …, …, …", not three.
   *
   * Diffed against what the SERVER held (`previousMembers`, read inside
   * `updateProjectMembers` moments before the write), never against the local
   * cache: the cache is what the panel composed this list FROM, so diffing
   * against it would describe the reviewer's intent rather than the change
   * that actually landed. An older write path that does not carry it records
   * nothing rather than a diff against an assumption.
   */
  if (previousMembers) {
    const change = describeMembershipChange(previousMembers, members);
    if (change) {
      yield* call(
        emitProjectAuditEvent,
        projectTdoId,
        change.action,
        change.details
      );
    }
  }
}

/**
 * Read a project's membership into the slice, for the panel's checkboxes.
 */
function* handleLoadProject(action: ReturnType<typeof LOAD_PROJECT_REQUEST>) {
  yield* put(
    readProjectMembers({ projectTdoId: action.payload.projectTdoId }) as never
  );
}

/**
 * Delete a request's merged document: the pointer first, then the TDO.
 */
function* handleDeleteProject(
  action: ReturnType<typeof DELETE_PROJECT_REQUEST>
) {
  const intl = sagaIntl();
  const { caseId, projectTdoId } = action.payload;

  // VE-31538. Also reachable from the folder view, and the most destructive
  // of the set-wide writes.
  if (!(yield* ensureSetUnlocked(projectTdoId))) {
    yield* put(DELETE_PROJECT_FAILURE({ caseId }));
    return;
  }

  const folder = yield* call(findRequestFolder, caseId);
  const caseSdoId = projectsSdoIdOfFolder(folder);
  const remaining = projectsOfFolder(folder).filter(
    (pointer) => pointer.tdoId !== projectTdoId
  );
  if (!caseSdoId) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentDeleteFailed',
          defaultMessage: 'Could not delete the merged document.',
        }),
        variant: 'error',
      })
    );
    yield* put(DELETE_PROJECT_FAILURE({ caseId }));
    return;
  }

  yield* put(writeProjectPointers({ caseSdoId, pointers: remaining }) as never);
  const recorded = yield* race({
    ok: take(forCaseSdo(WRITE_PROJECT_POINTERS_SUCCESS, caseSdoId)),
    err: take(forCaseSdo(WRITE_PROJECT_POINTERS_FAILURE, caseSdoId)),
  });
  if (!recorded.ok) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentDeleteFailed',
          defaultMessage: 'Could not delete the merged document.',
        }),
        variant: 'error',
      })
    );
    yield* put(DELETE_PROJECT_FAILURE({ caseId }));
    return;
  }

  // The request no longer has a merged document as far as anything that reads
  // it is concerned, so report success now and let the TDO delete follow. A
  // failure there is logged, not surfaced: the user asked for the merged
  // document to go, and it has.
  yield* put(
    DELETE_PROJECT_SUCCESS({ caseId, projectTdoId, pointers: remaining })
  );
  yield* put(deleteProjectTdo({ projectTdoId }) as never);
  const removed = yield* race({
    ok: take(DELETE_PROJECT_TDO_SUCCESS),
    err: take(DELETE_PROJECT_TDO_FAILURE),
  });
  if (!removed.ok) {
    console.warn(
      '[redact] merged document unlinked but its TDO was not deleted',
      projectTdoId
    );
  }
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'mergedDocumentDeleted',
        defaultMessage: 'Merged document deleted.',
      }),
      variant: 'success',
    })
  );
}

/**
 * Open a request's merged document.
 */
function* handleOpenProject(action: ReturnType<typeof OPEN_PROJECT_REQUEST>) {
  const intl = sagaIntl();
  const { caseId, projectTdoId } = action.payload;

  yield* put(readProjectMembers({ projectTdoId }) as never);
  const loaded = yield* race({
    // `handleLoadProject` is `takeEvery` and the folder view, the panel and
    // the editor all dispatch LOAD_PROJECT_REQUEST on their own schedules, so
    // an uncorrelated take here could resolve on another project's answer and
    // route the reviewer into that project's first member.
    ok: take(forProject(LOAD_PROJECT_SUCCESS, projectTdoId)),
    err: take(forProject(LOAD_PROJECT_FAILURE, projectTdoId)),
  });
  const firstMember = loaded.ok?.payload?.members?.[0];
  if (!firstMember) {
    // Either the read failed or the project has no members left — every one
    // of its documents was deleted from the request. Both leave nothing to
    // open, and silently doing nothing on a click is worse than saying so.
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentOpenFailed',
          defaultMessage: 'Could not open the merged document.',
        }),
        variant: 'error',
      })
    );
    return;
  }
  /**
   * LANDS ON THE MERGED DOCUMENT, not on one of its parts — and now SAYS so.
   *
   * It used to route to the first member with `?tab=redacted&combined=<id>`,
   * which named a file the reviewer did not ask for and broke the link the
   * moment that file left the set. The route saga resolves a project to a
   * host member itself, so the id it needs is the project's and the query is
   * empty: a project URL is the merged output by definition.
   *
   * The membership read above is still what makes this safe to attempt — it
   * is how an empty or unreadable set is caught and reported rather than
   * routed into.
   */
  yield* put(
    ROUTE_DOCUMENT_DETAILS({
      caseId,
      tdoId: projectTdoId,
    }) as never
  );
}

/**
 * Membership writes, one at a time, newest-wins.
 */
function* watchMembershipWrites() {
  const channel = yield* actionChannel(
    UPDATE_PROJECT_REQUEST,
    buffers.sliding(1)
  );
  while (true) {
    const action = yield* take(channel);
    yield* call(handleUpdateProject, action);
  }
}

export function* projectsSaga() {
  yield* takeLeading(CREATE_PROJECT_REQUEST, handleCreateProject);
  yield* takeLeading(OPEN_PROJECT_REQUEST, handleOpenProject);
  // SERIALISED THROUGH A SLIDING CHANNEL, which is the only strategy that
  // actually holds here. See `watchMembershipWrites`.
  yield* fork(watchMembershipWrites);
  yield* takeEvery(LOAD_PROJECT_REQUEST, handleLoadProject);
  yield* takeLeading(DELETE_PROJECT_REQUEST, handleDeleteProject);
  // Bates writes are NOT registered here. They share a body with the
  // watermarks, so they share the one write queue in `watchProjectRedaction`
  // — a `takeLeading` beside that queue would read a body the queue was
  // about to replace.
  yield* fork(watchProjectRedaction);
  // `projectAuditSaga` is NOT forked here. It is a root entry of its own, for
  // the reason its own comment gives: a throw in an audit watcher must not
  // restart this root and tear down the membership channel mid-write.
}

export default projectsSaga;
